<template>
  <form @submit.prevent="savePayment">
    <div class="row">
      <div class="col-12 mb-3">
        <label class="form-label">Due Amount</label>
        <input
          type="text"
          class="form-control"
          :value="formatCurrency(order?.due_amount || 0)"
          readonly
        >
      </div>

      <div class="col-12 mb-3">
        <label class="form-label">Payment Amount</label>
        <input
          v-model.number="form.payment_amount"
          type="number"
          class="form-control"
          :class="{ 'is-invalid': errors.payment_amount }"
          step="0.01"
          min="0"
          :max="order?.due_amount || 0"
        >
        <div v-if="errors.payment_amount" class="invalid-feedback">
          {{ errors.payment_amount[0] }}
        </div>
      </div>

      <div class="col-12 mb-3">
        <label class="form-label">Payment Date</label>
        <input
          v-model="form.payment_date"
          type="date"
          class="form-control"
          :class="{ 'is-invalid': errors.payment_date }"
        >
        <div v-if="errors.payment_date" class="invalid-feedback">
          {{ errors.payment_date[0] }}
        </div>
      </div>

      <div class="col-12 mb-3">
        <label class="form-label">Payment Method</label>
        <select
          v-model="form.payment_method"
          class="form-select"
          :class="{ 'is-invalid': errors.payment_method }"
        >
          <option value="cash">Cash</option>
          <option value="bank">Bank Transfer</option>
          <option value="mobile_banking">Mobile Banking</option>
        </select>
        <div v-if="errors.payment_method" class="invalid-feedback">
          {{ errors.payment_method[0] }}
        </div>
      </div>

      <div class="col-12 mb-3">
        <label class="form-label">Note</label>
        <textarea
          v-model="form.note"
          class="form-control"
          :class="{ 'is-invalid': errors.note }"
          rows="3"
          placeholder="Payment note (optional)"
        ></textarea>
        <div v-if="errors.note" class="invalid-feedback">
          {{ errors.note[0] }}
        </div>
      </div>

      <div class="col-12">
        <button type="button" class="btn btn-secondary me-2" data-bs-dismiss="modal">
          Close
        </button>
        <button type="submit" class="btn btn-primary" :disabled="loading">
          <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
          Save Payment
        </button>
      </div>
    </div>
  </form>
</template>

<script>
import { showSuccessMessage, showErrorMessage } from '../utils/notifications.js';

export default {
  name: 'OrderPayment',

  props: {
    order: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      form: {
        payment_amount: 0,
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'cash',
        note: ''
      },
      errors: {},
      loading: false
    };
  },

  watch: {
    order: {
      handler(newOrder) {
        if (newOrder && newOrder.due_amount) {
          // Set default payment amount to due amount
          this.form.payment_amount = newOrder.due_amount;
        }
      },
      immediate: true
    }
  },

  mounted() {
    console.log('OrderPayment component mounted');
    console.log('Order data:', this.order);

    // Mark element as mounted
    this.$el.closest('[data-vue-component]')?.classList.add('vue-mounted');
  },

  methods: {
    async savePayment() {
      this.loading = true;
      this.errors = {};

      try {
        // Validate payment amount
        if (this.form.payment_amount > (this.order?.due_amount || 0)) {
          this.errors.payment_amount = ['Payment amount cannot exceed due amount'];
          this.loading = false;
          return;
        }

        if (this.form.payment_amount <= 0) {
          this.errors.payment_amount = ['Payment amount must be greater than 0'];
          this.loading = false;
          return;
        }

        const response = await this.$http.post(`/api/orders/${this.order.id}/payment`, {
          payment_amount: this.form.payment_amount,
          payment_date: this.form.payment_date,
          payment_method: this.form.payment_method,
          note: this.form.note
        });

        if (response.data.success) {
          this.showSuccessMessage(response.data.message);

          // Reset form
          this.resetForm();

          // Close modal
          const modal = this.$el.closest('.modal');
          if (modal) {
            const modalInstance = bootstrap.Modal.getInstance(modal);
            if (modalInstance) {
              modalInstance.hide();
            }
          }

          // Emit event for parent components
          this.$emit('payment-saved', response.data.order);

          // Redirect if specified
          if (response.data.redirect_url) {
            window.location.href = response.data.redirect_url;
          } else {
            // Refresh page to show updated order
            window.location.reload();
          }
        }
      } catch (error) {
        console.error('Payment save failed:', error);

        if (error.response?.status === 422) {
          this.errors = error.response.data.errors || {};
          this.showErrorMessage('Please check the form for errors');
        } else {
          this.showErrorMessage(
            error.response?.data?.message || 'An error occurred while saving the payment'
          );
        }
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.form = {
        payment_amount: 0,
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'cash',
        note: ''
      };
      this.errors = {};
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount || 0);
    },

    showSuccessMessage,
    showErrorMessage
  }
}
</script>

<style scoped>
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: #dc3545;
}
</style>
